import { motion, AnimatePresence } from "framer-motion";
import { Share, Plus, X } from "lucide-react";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { useInstallStore } from "@/pwa/installStore";

const EASE = [0.22, 1, 0.36, 1] as const;

// Section 6 — real PWA install experience.
// Android/desktop: native beforeinstallprompt. iOS Safari: Add-to-Home-Screen guide.
export function InstallPrompt() {
  const platform = useInstallStore((s) => s.platform);
  const isStandalone = useInstallStore((s) => s.isStandalone);
  const isInstalled = useInstallStore((s) => s.isInstalled);
  const canPromptNative = useInstallStore((s) => s.canPromptNative);
  const bannerDismissed = useInstallStore((s) => s.bannerDismissed);
  const iosSheetOpen = useInstallStore((s) => s.iosSheetOpen);
  const promptInstall = useInstallStore((s) => s.promptInstall);
  const openIos = useInstallStore((s) => s.openIos);
  const closeIos = useInstallStore((s) => s.closeIos);
  const dismissBanner = useInstallStore((s) => s.dismissBanner);

  const showBanner = !isStandalone && !isInstalled && !bannerDismissed && (canPromptNative || platform === "ios");
  const onInstall = () => (platform === "ios" ? openIos() : promptInstall());

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            data-testid="install-prompt"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed inset-x-4 bottom-24 z-30 mx-auto max-w-md rounded-2xl border border-cad/40 bg-obsidian-deep/90 p-5 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur-2xl md:inset-x-auto md:bottom-6 md:left-auto md:right-6"
            style={{ marginBottom: "env(safe-area-inset-bottom)" }}
          >
            <button type="button" data-testid="install-prompt-close" aria-label="Dismiss" onClick={dismissBanner} className="absolute right-3 top-3 text-cad transition-colors duration-200 hover:text-white">
              <X size={16} />
            </button>
            <BuildEaseLogo height={30} />
            <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-white">Take BuildEase with you</h3>
            <p className="mt-1 font-sans text-sm leading-relaxed text-white/60">
              Install BuildEase on your device for convenient access directly from your home screen.
            </p>
            <div className="mt-5 flex items-center gap-4">
              <button type="button" data-testid="install-prompt-install" onClick={onInstall} className="rounded-xl bg-safety px-6 py-3 font-display text-sm font-semibold text-obsidian transition-opacity duration-200 hover:opacity-90">
                Install BuildEase
              </button>
              <button type="button" data-testid="install-prompt-dismiss" onClick={dismissBanner} className="font-mono text-[11px] uppercase tracking-[0.16em] text-cad transition-colors duration-200 hover:text-white/70">
                Not now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <IosInstructions open={iosSheetOpen} onClose={closeIos} />
    </>
  );
}

function IosStep({ n, children, icon }: { n: number; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-cad/40 font-mono text-[11px] text-white/70">{n}</span>
      <span className="flex items-center gap-2 font-sans text-sm text-white/80">{children}{icon}</span>
    </li>
  );
}

export function IosInstructions({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.button type="button" aria-label="Close" data-testid="ios-install-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-50 bg-obsidian/70 backdrop-blur-sm" />
          <motion.div
            role="dialog"
            aria-label="Add to Home Screen"
            data-testid="ios-install-instructions"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl border-t border-cad/40 bg-obsidian-deep p-6"
            style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
          >
            <span aria-hidden className="mx-auto mb-5 block h-1 w-10 rounded-full bg-cad/60" />
            <div className="mx-auto max-w-md">
              <BuildEaseLogo height={28} />
              <h3 className="mt-4 font-display text-xl font-semibold text-white">Add BuildEase to your Home Screen</h3>
              <p className="mt-1 font-sans text-sm leading-relaxed text-white/60">
                Get convenient access to BuildEase directly from your iPhone or iPad.
              </p>
              <ol className="mt-6 flex flex-col gap-4">
                <IosStep n={1} icon={<Share size={16} className="text-safety" />}>Tap the Share button in Safari</IosStep>
                <IosStep n={2} icon={<Plus size={16} className="text-safety" />}>Select "Add to Home Screen"</IosStep>
                <IosStep n={3}>Confirm by tapping "Add"</IosStep>
              </ol>
              <button type="button" data-testid="ios-install-done" onClick={onClose} className="mt-7 w-full rounded-2xl bg-safety py-3.5 font-display text-sm font-semibold text-obsidian transition-opacity duration-200 hover:opacity-90">
                Got it
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
